import { Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { unflatten } from 'flat';
import { BehaviorSubject, Subject } from 'rxjs';
import { deepFlatten } from '../../../utils/utils/deep-flatten.utils';
import { EIremboFormlyFormStateKeys } from '../../models/irembo-formly-formstate-keys.enum';
import { TranslateService } from '@ngx-translate/core';
import {
  getParentRepeaterKeyAndItemIndex,
  IRepeaterIndexKeys,
} from '../../../utils/utils/formly.utils';

export interface GlobalErrorState {
  message: string;
  field?: string;
  type?: 'error' | 'warning' | 'info';
}

export interface FetchSuccessEvent {
  fetchKey: string;
  componentId: string;
  response: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class FormStateService {
  private formlyFormState$: BehaviorSubject<any> = new BehaviorSubject<any>({});
  private _uploadedFiles: { fileName: string; hash: string }[] = [];

  // Fetch event coordination for conditional data fetch components
  private fetchRegistry = new Map<string, string>();
  private fetchSuccessSubject = new Subject<FetchSuccessEvent>();
  fetchSuccess$ = this.fetchSuccessSubject.asObservable();

  constructor(private translateService: TranslateService) {}

  // Expose the translate service through a getter
  get translate() {
    return this.translateService;
  }

  // Convenience method for translations
  getTranslation(key: string, params?: any) {
    return this.translateService.instant(key, params);
  }

  private intializeRepeater$ = new BehaviorSubject<Record<string, unknown>>({});
  intializeRepeaterSource$ = this.intializeRepeater$.asObservable();

  getFormlyFormStateSubject(): BehaviorSubject<any> {
    return this.formlyFormState$;
  }

  getCurrentFormlyFormState(): any {
    return this.formlyFormState$.value;
  }

  setFormlyFormState(formState: any): void {
    this.formlyFormState$.next({ ...formState });
  }

  partialUpdateFormlyFormState(formStatePartial: any): void {
    this.formlyFormState$.next({
      ...this.getCurrentFormlyFormState(),
      ...formStatePartial,
    });
  }

  saveFetchedDataKeyInFormState = (
    field: FormlyFieldConfig,
    data: any,
    formStateKey: EIremboFormlyFormStateKeys = EIremboFormlyFormStateKeys.FETCHED_DATA,
    deleteWhenInvalid = true
  ) => {
    let fetchedData = undefined;
    try {
      let jsonData: Record<string, unknown> | undefined = undefined;
      if (data && typeof data === 'string') {
        jsonData = JSON.parse(data);
      }

      if (data && typeof data === 'object') {
        jsonData = data as Record<string, unknown>;
      }

      if (jsonData !== undefined) {
        const flattenedData = deepFlatten(jsonData);
        fetchedData = unflatten(flattenedData);
      } else {
        fetchedData = data;
      }
    } catch (e) {
      fetchedData = data;
    }
    if (deleteWhenInvalid && field.formControl?.invalid) {
      this.deleteFetchedDataKeyInFormState(field);
      return;
    }
    let fetchedDataAsJson: Record<string, unknown> = {};
    if (fetchedData && typeof fetchedData === 'string') {
      fetchedDataAsJson = JSON.parse(fetchedData);
    }

    if (fetchedData && typeof fetchedData === 'object') {
      fetchedDataAsJson = fetchedData as Record<string, unknown>;
    }

    const formStateFetchedData: any =
      this.getCurrentFormlyFormState()?.[formStateKey] ?? {};

    const repeaterIndexKeys: IRepeaterIndexKeys | undefined =
      getParentRepeaterKeyAndItemIndex(field);

    if (repeaterIndexKeys) {
      this.setFetchedDataForRepeaterFields(
        field,
        formStateFetchedData,
        repeaterIndexKeys,
        fetchedDataAsJson
      );
    } else {
      formStateFetchedData[<string>field.key] = fetchedDataAsJson;
    }

    this.partialUpdateFormlyFormState({
      [formStateKey]: formStateFetchedData,
    });
  };

  private setFetchedDataForRepeaterFields(
    field: FormlyFieldConfig,
    formStateFetchedData: any,
    repeaterIndexKeys: IRepeaterIndexKeys,
    jsonData: Record<string, unknown>
  ): void {
    if (formStateFetchedData[repeaterIndexKeys.repeaterParentKey]) {
      if (
        formStateFetchedData[repeaterIndexKeys.repeaterParentKey]?.[
          repeaterIndexKeys.itemIndex
        ]
      ) {
        formStateFetchedData[repeaterIndexKeys.repeaterParentKey][
          repeaterIndexKeys.itemIndex
        ][<string>field?.key] = jsonData;
      } else {
        formStateFetchedData[repeaterIndexKeys.repeaterParentKey][
          repeaterIndexKeys.itemIndex
        ] = {
          [<string>field?.key]: jsonData,
        };
      }
    } else {
      formStateFetchedData[repeaterIndexKeys.repeaterParentKey] = [
        {
          [<string>field?.key]: jsonData,
        },
      ];
    }
  }

  checkIfApplicationInAnRFAState = (): boolean => {
    const applicationState =
      this.getCurrentFormlyFormState()?.[
        EIremboFormlyFormStateKeys.APPLICATION_STATE
      ];
    if (!applicationState) {
      return false;
    }
    return applicationState?.includes?.('PENDING_RESUBMISSION');
  };

  deleteFetchedDataKeyInFormState = (
    field: FormlyFieldConfig,
    formStateKey: EIremboFormlyFormStateKeys = EIremboFormlyFormStateKeys.FETCHED_DATA
  ) => {
    const currentFormState = this.getCurrentFormlyFormState();

    const repeaterIndexKeys = getParentRepeaterKeyAndItemIndex(field);

    if (
      repeaterIndexKeys &&
      currentFormState?.[formStateKey]?.[repeaterIndexKeys.repeaterParentKey]?.[
        repeaterIndexKeys.itemIndex
      ]?.[<string>field?.key]
    ) {
      delete currentFormState?.[formStateKey]?.[
        repeaterIndexKeys.repeaterParentKey
      ]?.[repeaterIndexKeys.itemIndex]?.[<string>field?.key];

      this.setFormlyFormState(currentFormState);
      return;
    }

    if (currentFormState?.[formStateKey]?.[<string>field?.key]) {
      delete currentFormState?.[formStateKey]?.[<string>field?.key];
      this.setFormlyFormState(currentFormState);
    }
  };

  deleteRepeaterKeyIndexFetchedDataInFormState = (
    index: number,
    field: FormlyFieldConfig,
    formStateKey: EIremboFormlyFormStateKeys = EIremboFormlyFormStateKeys.FETCHED_DATA
  ) => {
    const currentFormState = this.getCurrentFormlyFormState();
    if (
      Array.isArray(currentFormState?.[formStateKey]?.[<string>field?.key]) &&
      currentFormState?.[formStateKey]?.[<string>field?.key]?.[index]
    ) {
      currentFormState?.[formStateKey]?.[<string>field?.key]?.splice(index, 1);
      this.setFormlyFormState(currentFormState);
    }
  };

  intializeRepeater(value: Record<string, unknown>) {
    this.intializeRepeater$.next(value);
  }

  addGlobalError(field: FormlyFieldConfig, errorMessage: string) {
    const currentState = { ...this.getCurrentFormlyFormState() };
    const fieldKey = field.key as string;

    const error = {
      message: errorMessage,
      field: fieldKey,
      timestamp: Date.now(),
      type: 'error',
    };

    // Store both old format (for backward compatibility) and new format
    const globalErrors = currentState.globalErrors || {};
    globalErrors[fieldKey] = error;

    this.partialUpdateFormlyFormState({
      ...currentState,
      globalError: error, // Keep for backward compatibility
      globalErrors: globalErrors, // New field-specific errors
    });
  }

  clearGlobalError(field?: FormlyFieldConfig) {
    const currentState = { ...this.getCurrentFormlyFormState() };

    if (field) {
      // Clear error for specific field only
      const fieldKey = field.key as string;
      const globalErrors = { ...currentState.globalErrors };

      if (globalErrors && globalErrors[fieldKey]) {
        delete globalErrors[fieldKey];

        // Update globalError for backward compatibility
        // Set it to the most recent remaining error, or null if none
        const remainingErrors = Object.values(globalErrors);
        const mostRecentError =
          remainingErrors.length > 0
            ? remainingErrors.reduce((latest: any, current: any) =>
                current.timestamp > latest.timestamp ? current : latest
              )
            : null;

        this.partialUpdateFormlyFormState({
          globalError: mostRecentError,
          globalErrors:
            Object.keys(globalErrors).length > 0 ? globalErrors : undefined,
        });
      }
    } else {
      // Clear all errors (backward compatibility)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { globalError, globalErrors, ...rest } = currentState;
      this.setFormlyFormState(rest);
    }
  }

  hasGlobalError(): boolean {
    return !!this.getCurrentFormlyFormState()?.['globalError'];
  }

  hasFieldError(field: FormlyFieldConfig): boolean {
    const currentState = this.getCurrentFormlyFormState();
    const fieldKey = field.key as string;
    return !!(
      currentState?.globalErrors?.[fieldKey] ||
      currentState?.globalError?.field === fieldKey
    );
  }

  saveHashAttachmentInArray(fileName: string, hash: string) {
    this._uploadedFiles.push({ fileName: fileName, hash: hash });
  }

  getUploadedFiles(): { fileName: string; hash: string }[] {
    return this._uploadedFiles;
  }

  removeHashAttachmentFromArray(fileName: string, hash: string): void {
    const index = this._uploadedFiles.findIndex(
      file => file.hash === hash && file.fileName === fileName
    );

    if (index !== -1) {
      // Remove only the first matching element
      this._uploadedFiles.splice(index, 1);
    }
  }

  isDuplicateFile(fileHash: string): boolean {
    const uploadedFiles = this.getUploadedFiles();
    const cleanHash = fileHash.trim().toLowerCase();

    const isDuplicate = uploadedFiles.some(file => {
      const stored = (file.hash || '').trim().toLowerCase();
      return stored === cleanHash;
    });
    return isDuplicate;
  }

  async computeSHA256(fileContent: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(fileContent);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  clearUploadedFiles(): void {
    this._uploadedFiles = [];
  }

  // Fetch component registration methods for conditional data fetch coordination
  registerFetchComponent(fetchKey: string, componentId: string): void {
    this.fetchRegistry.set(fetchKey, componentId);
  }

  emitFetchSuccess(event: FetchSuccessEvent): void {
    this.fetchSuccessSubject.next(event);
  }

  unregisterFetchComponent(fetchKey: string): void {
    this.fetchRegistry.delete(fetchKey);
  }
}
